import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import { MultiSelect } from 'primereact/multiselect';
import { Tooltip } from 'primereact/tooltip';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { formatDateOnly } from '../../../../../utils/dateUtils';
import { getCurrencySymbol } from '../../../../../utils/currencySymbol';
import { useGetPreApprovalHistoryQuery } from '../../../../../api';

/**
 * History dialog component for displaying pre-approval transaction history
 */
const HistoryDialog = ({ hideDialog, dialogVisible, preApproval }) => {
    const { th } = useLanguage();
    
    // State for history data
    const [historyData, setHistoryData] = useState([]);
    // State for visible columns - only include fields that exist in the API response
    const [visibleColumns, setVisibleColumns] = useState(['transactionDate', 'userName', 'action', 'status', 'description', 'preAppNo', 'chassisNo', 'failureDate', 'kmInfo']);
    // Reference for DataTable export
    const dt = useRef(null);

    // Skip query if no preApproval or not visible
    const shouldFetch = dialogVisible && preApproval && preApproval.id;

    // Fetch history data when dialog is opened and preApproval is available
    const {
        data: historyRecords,
        isLoading,
        isError,
        error
    } = useGetPreApprovalHistoryQuery(preApproval?.id, {
        skip: !shouldFetch,
        refetchOnMountOrArgChange: true
    });

    // Update history data when API response changes
    useEffect(() => {
        console.log('History Records received from API:', historyRecords);

        // The API's transformResponse has already extracted the data array
        // So historyRecords is already the array we need
        if (historyRecords) {
            console.log('Setting history data with records:', historyRecords);
            setHistoryData(historyRecords);
        } else {
            console.log('No history records received');
            setHistoryData([]);
        }
    }, [historyRecords]);

    // Format date for display
    const dateBodyTemplate = (rowData, field) => {
        const dateValue = rowData[field];

        if (!dateValue) return '-';

        try {
            const date = new Date(dateValue);

            if (isNaN(date.getTime())) {
                return '-';
            }

            return date.toLocaleDateString('tr-TR');
        } catch (error) {
            console.error(`Date parse error for field ${field}:`, dateValue);
            return '-';
        }
    };

    // Format user information
    const userBodyTemplate = (rowData) => {
        return rowData.userName || rowData.userId || th('preApproval.history.unknown');
    };

    // Format status information
    const statusBodyTemplate = (rowData) => {
        const statusMap = {
            10: th('preApproval.status.draft'),
            15: th('preApproval.status.returned'),
            20: th('preApproval.status.waitingApproval'),
            30: th('preApproval.status.accepted'),
            40: th('preApproval.status.rejected')
        };

        return statusMap[rowData.status] || rowData.status;
    };

    const periodicMaintanceBodyTemplate = (rowData) => {
        if (rowData.periodicMaintance === 1) {
            return th('preApproval.history.yes');
        } else if (rowData.periodicMaintance === 0) {
            return th('preApproval.history.no');
        } else {
            return '-';
        }
    };

    // Format currency for display
    const currencyBodyTemplate = (rowData, field) => {
        const value = rowData[field] ? rowData[field] : '';
        // Default to TRY if currency not specified
        const currencySymbol = rowData.currency ? getCurrencySymbol(rowData.currency) : getCurrencySymbol('TRY');
        return `${value} ${currencySymbol}`;
    };

    // Column definitions with localized headers - only include fields that exist in the API response
    const columns = [
        { field: 'transactionDate', header: th('preApproval.history.transactionDate'), body: (rowData) => dateBodyTemplate(rowData, 'transactionDate'), sortable: true },
        { field: 'userName', header: th('preApproval.history.user'), body: userBodyTemplate, sortable: true },
        { field: 'action', header: th('preApproval.history.action'), sortable: true },
        { field: 'status', header: th('preApproval.table.status'), body: statusBodyTemplate, sortable: true },
        { field: 'description', header: th('preApproval.history.description'), sortable: true },
        { field: 'preAppNo', header: th('preApproval.table.preApprovalNo'), sortable: true },
        { field: 'chassisNo', header: th('preApproval.form.chassisNoLabel'), sortable: true },
        { field: 'failureDate', header: th('preApproval.history.failureDate'), body: (rowData) => dateBodyTemplate(rowData, 'failureDate'), sortable: true },
        { field: 'kmInfo', header: th('preApproval.history.kmInfo'), sortable: true },
        { field: 'estimatedTrackPrice', header: th('preApproval.history.estimatedPartPrice'), body: (rowData) => currencyBodyTemplate(rowData, 'estimatedTrackPrice'), sortable: true },
        { field: 'estimatedLaborPrice', header: th('preApproval.history.estimatedLaborPrice'), body: (rowData) => currencyBodyTemplate(rowData, 'estimatedLaborPrice'), sortable: true },
        { field: 'otherPayments', header: th('preApproval.history.otherPayments'), body: (rowData) => currencyBodyTemplate(rowData, 'otherPayments'), sortable: true },
        { field: 'totalPrice', header: th('preApproval.history.totalPrice'), body: (rowData) => currencyBodyTemplate(rowData, 'totalPrice'), sortable: true },
        { field: 'periodicMaintance', header: th('preApproval.history.periodicMaintenance'), body: periodicMaintanceBodyTemplate, sortable: true },
        { field: 'periodicMaintanceDesc', header: th('preApproval.history.periodicMaintenanceDesc'), sortable: true },
        { field: 'mainCategoryName', header: th('preApproval.history.mainCategory'), sortable: true },
        { field: 'secondaryCategoryName', header: th('preApproval.history.secondaryCategory'), sortable: true },
        { field: 'subCategoryName', header: th('preApproval.history.subCategory'), sortable: true },
        { field: 'subPartCategoryName', header: th('preApproval.history.subPartCategory'), sortable: true },
        { field: 'failureModeName', header: th('preApproval.history.failureMode'), sortable: true },
        { field: 'custStatement', header: th('preApproval.history.customerStatement'), sortable: true },
        { field: 'causeOfailure', header: th('preApproval.history.failureReason'), sortable: true },
        { field: 'remedyOresults', header: th('preApproval.history.remedyResults'), sortable: true },
        { field: 'bosBranchCode', header: th('preApproval.history.branchCode'), sortable: true },
        { field: 'bosBranchSiteCode', header: th('preApproval.history.branchSiteCode'), sortable: true },
        { field: 'createdDate', header: th('preApproval.history.createdDate'), body: (rowData) => dateBodyTemplate(rowData, 'createdDate'), sortable: true },
        { field: 'modifiedDate', header: th('preApproval.history.modifiedDate'), body: (rowData) => dateBodyTemplate(rowData, 'modifiedDate'), sortable: true },
        { field: 'createdName', header: th('preApproval.history.createdName'), sortable: true },
        { field: 'modifiedName', header: th('preApproval.history.modifiedName'), sortable: true }
    ];

    // Column selection component
    const columnSelectionTemplate = () => {
        return (
            <MultiSelect
                value={visibleColumns}
                options={columns.map((col) => ({ label: col.header, value: col.field }))}
                onChange={(e) => setVisibleColumns(e.value)}
                optionLabel="label"
                placeholder={th('preApproval.history.selectColumns')}
                className="w-full"
                display="chip"
                maxSelectedLabels={3}
                selectedItemsLabel={th('preApproval.history.selectedItems')}
                panelStyle={{ width: '300px' }}
            />
        );
    };

    // Export to CSV
    const exportCSV = () => {
        dt.current.exportCSV();
    };

    // Header template with column selection and export buttons
    const headerTemplate = () => {
        return (
            <div className="flex flex-column sm:flex-row w-full justify-content-end align-items-stretch sm:align-items-center gap-2">
                <div className="w-full sm:w-auto">{columnSelectionTemplate()}</div>
                <Button type="button" icon="pi pi-file" label={th('preApproval.history.csv')} onClick={exportCSV} className="p-button-outlined w-full sm:w-auto" />
            </div>
        );
    };

    // Dialog footer with close button
    const renderDialogFooter = () => {
        return (
            <div className="flex justify-content-end gap-2">
                <Button label={th('preApproval.history.close')} icon="pi pi-times" outlined onClick={hideDialog} type="button" />
            </div>
        );
    };

    return (
        <Dialog visible={dialogVisible} style={{ width: '90%', maxWidth: '1200px' }} header={`${th('preApproval.history.title')} - ${preApproval?.preAppNo || ''}`} modal className="p-fluid" footer={renderDialogFooter()} onHide={hideDialog} maximizable>
            <Tooltip target=".export-buttons>button" position="bottom" />

            {isLoading && (
                <div className="flex justify-content-center">
                    <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" />
                </div>
            )}

            {isError && <div className="p-error">{th('preApproval.history.loadingError').replace('{0}', error?.message || th('preApproval.history.unknownError'))}</div>}

            {!isLoading && !isError && (!historyData || historyData.length === 0) && <div className="text-center p-4">{th('preApproval.history.noHistoryFound')}</div>}

            {!isLoading && !isError && historyData && historyData.length > 0 && (
                <DataTable
                    ref={dt}
                    value={historyData}
                    paginator
                    rows={10}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    className="p-datatable-gridlines"
                    showGridlines
                    emptyMessage={th('preApproval.history.noHistoryFound')}
                    sortField="transactionDate"
                    sortOrder={-1}
                    header={headerTemplate}
                    resizableColumns
                    columnResizeMode="expand"
                    scrollable
                    scrollHeight="flex"
                    size="small"
                    responsiveLayout="scroll"
                    exportFilename={th('preApproval.history.exportFilename').replace('{0}', preApproval?.preAppNo || 'detay')}
                >
                    {columns
                        .filter((col) => visibleColumns.includes(col.field))
                        .map((col) => (
                            <Column key={col.field} field={col.field} header={col.header} body={col.body} sortable={col.sortable} frozen={col.frozen} alignFrozen={col.alignFrozen} />
                        ))}
                </DataTable>
            )}
        </Dialog>
    );
};

export default HistoryDialog;
